import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, setStoredSessionToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Church, Shield, Calendar, HandCoins, Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { user, loading, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState("google"); // 'google' | 'password'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePasswordLogin = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });
      if (data.session_token) setStoredSessionToken(data.session_token);
      setUser(data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      // Re-check to populate the rest of the auth context cleanly
      await checkAuth();
      navigate("/", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join(", ") : "Sign-in failed");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen login-mesh flex items-center justify-center px-6 py-16">
      <div className="absolute inset-0 pointer-events-none [background-image:radial-gradient(rgba(66,82,82,0.05)_1px,transparent_1px)] [background-size:18px_18px] opacity-60" />
      <div className="relative w-full max-w-5xl grid lg:grid-cols-2 gap-10 items-center animate-fade-up">
        {/* Left — brand copy */}
        <div className="hidden lg:block text-brand-primary space-y-8 pr-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/60 backdrop-blur border border-brand-primary/10 text-xs uppercase tracking-[0.2em] text-terracotta font-semibold">
            <Church className="h-3.5 w-3.5" /> Staff Command Center
          </div>
          <h1 className="font-heading text-5xl xl:text-6xl font-semibold leading-[1.05]">
            One place for your<br />
            <span className="text-terracotta">whole church team.</span>
          </h1>
          <p className="text-lg text-brand-primary/75 max-w-md leading-relaxed">
            Giving, attendance, communications, documents, and calendars — brought together in a single, secure dashboard built for church staff.
          </p>

          <div className="grid grid-cols-3 gap-4 max-w-md">
            {[
              { icon: HandCoins, label: "Giving" },
              { icon: Calendar, label: "Calendar" },
              { icon: Shield, label: "Role-based" },
            ].map((f) => (
              <div
                key={f.label}
                className="rounded-2xl bg-white/70 backdrop-blur-md border border-brand-primary/10 p-4 shadow-warm"
              >
                <f.icon className="h-5 w-5 text-terracotta mb-3" strokeWidth={1.6} />
                <p className="text-sm font-medium">{f.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right — sign-in card */}
        <div className="relative">
          <div className="rounded-3xl bg-white/75 backdrop-blur-xl border border-white/60 shadow-warm-lg p-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-12 w-12 rounded-2xl bg-terracotta/15 grid place-items-center">
                <Church className="h-6 w-6 text-terracotta" strokeWidth={1.8} />
              </div>
              <div>
                <p className="font-heading text-xl font-semibold text-brand-primary">Family Chapel</p>
                <p className="text-xs uppercase tracking-[0.18em] text-brand-primary/60">Staff Portal</p>
              </div>
            </div>

            <h2 className="font-heading text-3xl font-semibold text-brand-primary mb-2">Welcome back.</h2>
            <p className="text-brand-primary/70 mb-6">
              {mode === "google"
                ? "Sign in with your church Google account to continue."
                : "Sign in with the email and password your admin set up for you."}
            </p>

            {mode === "google" ? (
              <Button
                data-testid="google-login-button"
                onClick={handleGoogleLogin}
                className="w-full h-14 rounded-full bg-terracotta hover:bg-terracotta-hover text-white text-base font-medium shadow-warm-lg transition-all duration-300 hover:shadow-warm-lg active:scale-[0.99]"
              >
                <svg className="mr-3 h-5 w-5" viewBox="0 0 24 24" fill="none">
                  <path d="M21.35 11.1h-9.17v2.92h5.29c-.23 1.48-1.73 4.34-5.29 4.34-3.18 0-5.77-2.63-5.77-5.86s2.59-5.86 5.77-5.86c1.8 0 3.01.77 3.7 1.43l2.52-2.43C16.8 4.23 14.78 3.3 12.18 3.3 6.99 3.3 2.8 7.48 2.8 12.5S6.99 21.7 12.18 21.7c7.02 0 9.33-4.92 9.33-7.44 0-.5-.06-.88-.16-1.16z" fill="#fff" />
                </svg>
                Continue with Google
              </Button>
            ) : (
              <form onSubmit={handlePasswordLogin} className="space-y-4" data-testid="password-login-form">
                <div>
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    data-testid="password-login-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoFocus
                    placeholder="you@familychapel.org"
                    className="mt-1 h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    data-testid="password-login-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="mt-1 h-11"
                  />
                </div>
                {error && (
                  <p className="text-sm text-terracotta" data-testid="password-login-error">{error}</p>
                )}
                <Button
                  type="submit"
                  data-testid="password-login-submit"
                  disabled={submitting}
                  className="w-full h-12 rounded-full bg-terracotta hover:bg-terracotta-hover text-white text-base font-medium"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in…</> : "Sign in"}
                </Button>
              </form>
            )}

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => { setMode(mode === "google" ? "password" : "google"); setError(""); }}
                data-testid="toggle-login-mode"
                className="text-sm text-brand-primary/65 hover:text-terracotta hover:underline transition-colors"
              >
                {mode === "google"
                  ? "Or sign in with email & password"
                  : "Back to Google sign-in"}
              </button>
            </div>

            <div className="mt-8 pt-6 border-t border-brand-primary/10 text-sm text-brand-primary/60 leading-relaxed">
              <p className="flex items-start gap-2">
                <Shield className="h-4 w-4 mt-0.5 shrink-0 text-sage" strokeWidth={1.8} />
                <span>Staff-only portal. Your admin controls who can sign in. New accounts must be approved before access.</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
